/**
 * Theming store — toggles the `.dark` class that theme.css/tailwind-preset
 * key their dark OKLCH tokens off of. Zustand, per this app's "Zustand for
 * client/UI state" convention (see use-toast.ts).
 */
import { create } from 'zustand';

import { type AccentPreset, accentPresets } from './tokens';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type { AccentPreset };

const STORAGE_KEY = 'workingclock:theme';
const ACCENT_STORAGE_KEY = 'workingclock:theme-accent';
const ACCENT_CLASSES = Object.keys(accentPresets).map((name) => `theme-${name}`);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function readStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // localStorage can throw (privacy mode, blocked site data) — fall back to system.
    return 'system';
  }
}

export function readStoredAccent(): AccentPreset {
  if (typeof window === 'undefined') return 'gold';
  try {
    const stored = window.localStorage.getItem(ACCENT_STORAGE_KEY);
    return stored && stored in accentPresets ? (stored as AccentPreset) : 'gold';
  } catch {
    return 'gold';
  }
}

function applyResolvedTheme(theme: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
}

function applyAccent(accent: AccentPreset): void {
  document.documentElement.classList.remove(...ACCENT_CLASSES);
  if (accent !== 'gold') document.documentElement.classList.add(`theme-${accent}`);
}

interface ThemeStoreState {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  accent: AccentPreset;
  setMode(mode: ThemeMode): void;
  setAccent(accent: AccentPreset): void;
}

export const useThemeStore = create<ThemeStoreState>((set) => ({
  mode: 'system',
  resolvedTheme: 'light',
  accent: 'gold',
  setMode(mode) {
    const resolvedTheme = mode === 'system' ? getSystemTheme() : mode;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // localStorage unavailable — the theme still applies for this session.
      }
      applyResolvedTheme(resolvedTheme);
    }
    set({ mode, resolvedTheme });
  },
  setAccent(accent) {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(ACCENT_STORAGE_KEY, accent);
      } catch {
        // Same as setMode — apply without persisting.
      }
      applyAccent(accent);
    }
    set({ accent });
  },
}));

export function useTheme(): {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  accent: AccentPreset;
  setMode(mode: ThemeMode): void;
  setAccent(accent: AccentPreset): void;
} {
  const mode = useThemeStore((s) => s.mode);
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const accent = useThemeStore((s) => s.accent);
  const setMode = useThemeStore((s) => s.setMode);
  const setAccent = useThemeStore((s) => s.setAccent);
  return { mode, resolvedTheme, accent, setMode, setAccent };
}
